<template>
    <div v-if="calculating" class="row q-pa-md justify-center"><q-spinner color="secondary"/></div>
    <div v-else ref="root" id="RestorationPane" class="restoration-pane q-pa-sm" v-bind="attrs">
        <div>This includes a selection of restoration, mitigation, and other projects that have that have been completed, are ongoing, or are planned for the area selected. These are projects that benefit the coastal habitats and/or the species within the area. This list was intended to highlight the actions and organizations already working in this space.</div>
        <div class="text-caption q-mt-xs">NOTE: This is NOT an exhaustive list, as data were not available for all projects. <a href='https://forms.office.com/pages/responsepage.aspx?id=wW2-eY7Xu0uyK9mUwKQXp98W1OHcGVtJj2bf9co0xNpUNThLWTFXUFFQU1I3MkQ3MzJGRkhBQTE3WS4u&route=shorturl' target='_blank'>Contact us</a> if you have projects to add.</div>
        <div v-if="projects.length > 0">

            <div class="text-body1 q-mt-sm">Total number of projects: <span class="text-bold">{{ projects.length  }}</span>
                <q-btn dense  size="xs" flat class="gray q-ml-sm q-mb-sm" icon="help_outline" @click.stop="infoDialogs['numProjects'].model = !infoDialogs['numProjects'].model" aria-label="Number of projects help"/>
            </div>

            <q-expansion-item dense group="projs" label="Projects By Habitat" header-class="bg-grey-3 text-bold" class="proj-expander q-mt-sm q-ml-md q-ma-xs">
                <div class="text-caption q-ma-sm">The bars below summarize all projects within the AOI by the habitat type that was targeted. Click on each bar to highlight the projects on the map and in the project details below.</div>
                <div class="bar-grid q-pa-xs q-pl-md">
                    <div v-for="(projs, habitat) in projectsByHabitatType" :key="habitat"
                        class="bar-item pointer"
                        :class="{ 'bar-item-selected': isLabelSelected('habitat', habitat) }"
                        @click="highlightProjects('habitat', habitat)"
                    >
                        <div class="bar-row q-pa-xs shadow-1">
                            <div class="bar-track">
                                <div class="bar-fill" :style="{width: (projs.length / projects.length * 100) + '%'}"></div>
                            </div>
                            <span class="bar-meta">{{ projs.length }} {{ habitat }}</span>
                        </div>
                    </div>
                </div>
            </q-expansion-item>
            <q-expansion-item group="projs" dense label="Projects by Species Benefited" header-class="bg-grey-3 text-bold" class="proj-expander q-ml-md q-ma-xs">
                <div class="text-caption q-ma-sm">The bars below summarize all projects within the AOI by the species or species groups targeted. Click on each bar to highlight the projects on the map and in the project details below.</div>
                <div class="q-pa-xs  q-pl-md">
                    <template v-for="group in ['Plants', 'Fish', 'Birds', 'Shellfish', 'Other']" :key="group">
                        <div v-if="Object.keys(projectsBySpeciesGrouped[group]).length > 0"
                            class="text-caption text-uppercase text-weight-bold q-mt-md q-ml-xs text-secondary">
                            {{ group }}
                        </div>
                        <div class="bar-grid">
                            <div v-for="(projs, spp) in projectsBySpeciesGrouped[group]" :key="spp"
                                class="bar-item pointer"
                                :class="{ 'bar-item-selected': isLabelSelected('species', spp) }"
                                @click="highlightProjects('species', spp)"
                            >
                                <div class="bar-row q-pa-xs shadow-1">
                                    <div class="bar-track">
                                        <div class="bar-fill" :style="{width: (projs.length / projects.length * 100) + '%'}"></div>
                                    </div>
                                    <span class="bar-meta">{{ projs.length }} {{ spp }}</span>
                                    <span v-if="spCodes2RestorationCodes[getKeyByValue(restoration_spp, spp)] && Object.keys(Habitats).includes(spCodes2RestorationCodes[getKeyByValue(restoration_spp, spp)])">
                                        <q-icon name="search" class="circle pointer" size="xs" :style="{'background-color' : '#008472'}" color="white" @click.stop="goToSpSpotlight(spCodes2RestorationCodes[getKeyByValue(restoration_spp, spp)])"/>
                                        <q-tooltip>Go to the Species Spotlight section for {{spp}}</q-tooltip>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </q-expansion-item>

            <q-expansion-item group="projs" dense label="Projects By Type" header-class="bg-grey-3 text-bold" class="proj-expander q-ml-md q-ma-xs">
                <div class="text-caption q-ma-sm">The bars below summarize all projects within the AOI by project type. Click on each bar to highlight the projects on the map and in the project details below.</div>
                <div class="bar-grid q-pa-sm q-pl-md">
                    <div v-for="(projs, type) in projectsByType" :key="type"
                        class="bar-item pointer"
                        :class="{ 'bar-item-selected': isLabelSelected('type', type) }"
                        @click="highlightProjects('type', type)"
                    >
                        <div class="bar-row q-pa-xs shadow-1">
                            <div class="bar-track">
                                <div class="bar-fill" :style="{width: (projs.length / projects.length * 100) + '%'}"></div>
                            </div>
                            <span class="bar-meta">{{ projs.length }} {{ type }}</span>
                        </div>
                    </div>
                </div>
            </q-expansion-item>
            
            <q-expansion-item v-model="projectDetailsOpen" expand-icon-toggle  dense label="Project Details" class="q-mt-sm bg-secondary proj-det-expander q-ml-md" header-class="text-white" expand-icon-class="text-white" >
                <q-card class="details-card">
                    <q-card-section class="q-pa-xs">
                        <template v-for="group in projectsGrouped" :key="group.baseName">
                            <!-- Group wrapper for sequentially-numbered projects -->
                            <q-expansion-item
                                v-if="group.isGroup"
                                dense
                                expand-icon-class="ml-auto"
                                :header-class="groupHeaderClass(group.projects)"
                                group="details"
                            >
                                <template v-slot:header>
                                    <div class="project-detail-header row items-center no-wrap full-width">
                                        <div class="project-detail-title col ellipsis">{{ group.baseName }}</div>
                                        <div class="project-detail-meta flex items-center no-wrap">
                                        <q-badge color="grey-7" class="q-ml-sm">{{ group.projects.length }}</q-badge>
                                        <q-tooltip >{{ group.projects.length }} {{ group.baseName }} within the AOI</q-tooltip>
                                        </div>
                                    
                                    </div>
                                </template>
                                <div v-for="proj in group.projects" :key="proj.id" class="q-pl-lg">
                                    <q-expansion-item
                                        class=""
                                        dense
                                        group="details-sub"
                                        v-model="projectDetailOpenState[proj.id]"
                                        :header-class="projectHeaderClass(proj)"
                                        expand-icon-class="ml-auto "
                                        @update:model-value="(val) => val ? showProjectOnMap(proj) : clearProjectOnMap(proj)"
                                    >
                                        <template v-slot:header>{{ proj.uid?.Name }}</template>
                                        <ul>
                                            <template v-for="attr in attrOrder" :key="attr">
                                                <li v-if="proj.uid && proj.uid[attr] && (!hideMetrics.includes(String(attr)) || showTextMetrics.includes(String(attr)))">
                                                    <span v-if="typeof proj.uid[attr] === 'string' && !proj.uid[attr].startsWith('http')"><span class='text-bold'>{{ attrNames[attr] }}</span>: {{ displayMetric(attr, proj.uid[attr]) }}</span>
                                                    <span v-else-if="typeof proj.uid[attr] === 'string' && proj.uid[attr].startsWith('http')"><span class='text-bold'>{{ attrNames[attr] }}</span>: <a :href="proj.uid[attr]" target="_blank">Link</a></span>
                                                    <span v-else><span class='text-bold'>{{ attrNames[attr] }}</span>: {{ displayMetric(attr, proj.uid[attr]) }}</span>
                                                </li>
                                            </template>
                                            <li v-if="!hasLocation(proj)" class="text-italic text-grey-7">No location data available</li>
                                        </ul>
                                        <div class="q-pa-xs">
                                            <q-btn flat dense size="sm" color="primary" label="More details" icon="info" @click.stop="openL2Dialog(proj)" />
                                        </div>
                                    </q-expansion-item>
                                </div>
                            </q-expansion-item>
                            <!-- Single project (no sequential numbering) -->
                            <div v-else >
                                <q-expansion-item
                                    dense
                                    group="details"
                                    v-model="projectDetailOpenState[group.projects[0].id]"
                                    :header-class="projectHeaderClass(group.projects[0])"
                                    expand-icon-class="ml-auto"
                                    @update:model-value="(val) => val ? showProjectOnMap(group.projects[0]) : clearProjectOnMap(group.projects[0])"
                                >
                                    <template v-slot:header>
                                        <div class="project-detail-header row items-center no-wrap full-width">
                                            <div class="project-detail-title col ellipsis">{{ group.projects[0].uid?.Name }}</div>
                                        </div>
                                    </template>
                                    <ul>
                                        <template v-for="attr in attrOrder" :key="attr">
                                            <li v-if="group.projects[0].uid && group.projects[0].uid[attr] && (!hideMetrics.includes(String(attr)) || showTextMetrics.includes(String(attr)))">
                                                <span v-if="typeof group.projects[0].uid[attr] === 'string' && !group.projects[0].uid[attr].startsWith('http')"><span class='text-bold'>{{ attrNames[attr] }}</span>: {{ displayMetric(attr, group.projects[0].uid[attr]) }}</span>
                                                <span v-else-if="typeof group.projects[0].uid[attr] === 'string' && group.projects[0].uid[attr].startsWith('http')"><span class='text-bold'>{{ attrNames[attr] }}</span>: <a :href="group.projects[0].uid[attr]" target="_blank">Link</a></span>
                                                <span v-else><span class='text-bold'>{{ attrNames[attr] }}</span>: {{ displayMetric(attr, group.projects[0].uid[attr]) }}</span>
                                            </li>
                                        </template>
                                        <li v-if="!hasLocation(group.projects[0])" class="text-italic text-grey-7">No location data available</li>
                                    </ul>
                                    <div class="q-pa-xs">
                                        <q-btn flat dense size="sm" color="primary" label="More details" icon="info" @click.stop="openL2Dialog(group.projects[0])" />
                                    </div>
                                </q-expansion-item>
                            </div>
                        </template>
                    </q-card-section>
                </q-card>
            </q-expansion-item>
        </div>
        <div v-else-if="!calculating">
            No coastal conservation projects were identified within the area of interest.
        </div>
    </div>

    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model">
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>

    <q-dialog v-model="l2DialogOpen">
        <InfoDialog :html="l2DialogHtml" :name="l2DialogTitle" />
    </q-dialog>
</template>
<script setup lang="ts">
// @ts-nocheck
import { toRefs, ref, reactive, shallowRef, computed, watch, onMounted, useAttrs, onBeforeUnmount } from "vue";
import { useHelpers } from "../composables/useHelpers"
import { useMapItems } from "../composables/useMapItems"
import InfoDialog from "./InfoDialog.vue";
import { habitat_types } from "../assets/restoration_habitat.json";
import  { restoration_projtype } from "../assets/restoration_projtype.json";
import  { restoration_spp } from "../assets/restoration_spp.json";
import  { restoration_src } from "../assets/restoration_src.json";
import  { restoration_status } from "../assets/restoration_status.json";
import { spCodes2RestorationCodes } from "../assets/spCodes2RestorationCodes.json"
import l2AttrNamesJson from "../assets/restoration_l2_attr_names.json"
import { Habitats } from "../assets/SpSpotText.json" 

import { useArrowTable } from "../composables/useArrowTables";
import { useRouter } from 'vue-router'

const router = useRouter()
const attrs = useAttrs()
const calculating = ref(true)
const { setActiveSpSpotlight, tileLayers, toggleLayer, print, printing, ecos, styleMap, clearMapStyle, setProcessedData, processedData, loadDataByID, activeLayerID, showRestorationProjectPoint, clearRestorationProjectPoint, showRestorationProjectHighlightPoints, clearRestorationProjectHighlightPoints } = useMapItems()
const { arrayFindObjectByProp, sumArray, getKeyByValue } = useHelpers();
const { loadArrow, loading, error } = useArrowTable();

const restorationMap = shallowRef<Map<string, any>>(new Map());

const root = ref<HTMLElement | null>(null)

function clearHighlight() {
  selectedHighlight.value = null
  // Only clear the highlighted project markers; leave map styling unchanged.
  clearRestorationProjectHighlightPoints()
}
function onDocumentClick(event: MouseEvent) {
  if (!root.value) return
  if (!root.value.contains(event.target as Node)) {
    clearHighlight()
  }
}

const props = defineProps({
    data: { type: Object },
    version: {type: Number}
})

const { data, version } = toRefs(props)

// describe the shape stored in our computed arrays
interface ProjectEntry {
    id: string;
    uid: any; // refine later if you know the JSON structure
}

const hideMetrics = ['Latitude', 'Longitude', 'Unnamed: 0', 'Species_Benefited', 'Habitats', 'Project_Status']
const showTextMetrics = ['PTID', 'HabID', 'SPP']

const selectedHighlight = ref<{category: 'habitat' | 'species' | 'type'; code: string} | null>(null)
const lastProjectId = ref<string | null>(null)
const projectDetailsOpen = ref(false)
const projectDetailOpenState = reactive<Record<string, boolean>>({})

function hasLocation(proj: ProjectEntry) {
    const lat = proj.uid?.Latitude
    const lng = proj.uid?.Longitude
    if (lat == null || lat === '' || lng == null || lng === '') return false
    return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
}

function showProjectOnMap(proj: ProjectEntry) {
    if (!hasLocation(proj)) return
    const lat = Number(proj.uid.Latitude)
    const lng = Number(proj.uid.Longitude)
    // Remove any previous project marker, then add the new one
    clearRestorationProjectPoint()
    // Default zoom + duration
    showRestorationProjectPoint(lat, lng, 10)
    lastProjectId.value = proj.id
}

function clearProjectOnMap(proj: ProjectEntry) {
    if (lastProjectId.value === proj.id) {
        clearRestorationProjectPoint()
        lastProjectId.value = null
    }
}

const habitatLabelToCode = computed(() => {
    const m = new Map<string, string>()
    for (const [code, label] of Object.entries(habitat_types)) {
        m.set(label, code)
    }
    return m
})

const speciesLabelToCode = computed(() => {
    const m = new Map<string, string>()
    for (const [code, label] of Object.entries(restoration_spp)) {
        m.set(label, code)
    }
    return m
})

const typeLabelToCode = computed(() => {
    const m = new Map<string, string>()
    for (const [code, label] of Object.entries(restoration_projtype)) {
        m.set(label, code)
    }
    return m
})

function highlightProjects(category: 'habitat' | 'species' | 'type', label: string) {
    const code =
        category === 'habitat'
            ? habitatLabelToCode.value.get(label)
            : category === 'species'
            ? speciesLabelToCode.value.get(label)
            : typeLabelToCode.value.get(label)

    selectedHighlight.value = code ? { category, code } : null

    if (!selectedHighlight.value) {
        clearRestorationProjectHighlightPoints()
        return
    }

    const toShow = projects.value
        .filter(projectMatchesHighlight)
        .map((proj) => ({
            lat: Number(proj.uid?.Latitude),
            lng: Number(proj.uid?.Longitude),
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))

    showRestorationProjectHighlightPoints(toShow)

    
}



const getRowsForCell = async (cellId: string) => {
  console.log('getting rows for', cellId)
  let lupTable, attsTable;
  try {
    ;[lupTable, attsTable] = await Promise.all([
      loadArrow(lupUrl),
      loadArrow(attsUrl)
    ]);
  } catch (e) {
    console.error('loadArrow failed:', e, { lupUrl, attsUrl })
    return []
  }

  // Find UIDs for this cell — the UID column is a comma-separated string per row
  const lupGridCol = lupTable.getChildAt(lupTable.schema.fields.findIndex(f => f.name === 'GRID_ID'));
  const lupUidCol  = lupTable.getChildAt(lupTable.schema.fields.findIndex(f => f.name === 'UID'));
  const uids: string[] = [];
  for (let i = 0; i < lupTable.numRows; i++) {
    if (String(lupGridCol.get(i)) === String(cellId)) {
      // UID cell is e.g. "MaPSRb49, MaPSRb50, MaPSRc97" — split into individual IDs
      const raw = String(lupUidCol.get(i));
      raw.split(',').forEach(u => uids.push(u.trim()));
      break; // GRID_ID is unique, no need to keep scanning
    }
  }
  console.log(uids)
  if (uids.length === 0) return [];

  // Get project details for those UIDs from the attributes table
  const attsUidCol = attsTable.getChildAt(attsTable.schema.fields.findIndex(f => f.name === 'UID'));
  const results: Record<string, any>[] = [];
  const uidSet = new Set(uids);
  for (let i = 0; i < attsTable.numRows; i++) {
    const uid = String(attsUidCol.get(i));
    if (uidSet.has(uid)) {
      const row: Record<string, any> = {};
      for (const field of attsTable.schema.fields) {
        row[field.name] = attsTable.getChildAt(attsTable.schema.fields.indexOf(field)).get(i);
      }
      results.push(row);
    }
  }
  console.log(results)
  return results;
}



function selectedLabelCode(category: 'habitat' | 'species' | 'type', label: string) {
    if (category === 'habitat') return habitatLabelToCode.value.get(label)
    if (category === 'species') return speciesLabelToCode.value.get(label)
    return typeLabelToCode.value.get(label)
}

function isLabelSelected(category: 'habitat' | 'species' | 'type', label: string) {
    if (!selectedHighlight.value) return false
    const code = selectedLabelCode(category, label)
    return (
        selectedHighlight.value.category === category &&
        code != null &&
        selectedHighlight.value.code === code
    )
}

function projectMatchesHighlight(proj: ProjectEntry) {
    if (!selectedHighlight.value) return false
    if (!proj.uid) return false
    const { category, code } = selectedHighlight.value

    if (category === 'habitat') {
        const val = proj.uid.HabID
        if (typeof val === 'string') {
            return val.split(',').map(s => s.trim()).includes(code)
        }
        return val == code
    }

    if (category === 'species') {
        const val = proj.uid.SPP
        if (typeof val === 'string') {
            return val.split(',').map(s => s.trim()).includes(code)
        }
        return val == code
    }

    if (category === 'type') {
        return proj.uid.PTID == code
    }

    return false
}

function displayMetric(attr: string, val: any) {
    if (attr === 'PTID') {
        return restoration_projtype[val] ?? val
    }
    if (attr === 'HabID') {
        if (typeof val === 'string') {
            return val
                .split(',')
                .map((code) => habitat_types[code.trim()] ?? code.trim())
                .join(', ')
        }
        return habitat_types[val] ?? val
    }
    if (attr === 'SPP') {
        if (typeof val === 'string') {
            return val
                .split(',')
                .map((code) => restoration_spp[code.trim()] ?? code.trim())
                .join(', ')
        }
        return restoration_spp[val] ?? val
    }
    if (attr === 'SRCE') {
        return restoration_src[val] ?? val
    }
    if (attr === 'STATUS') {
        return restoration_status[val] ?? val
    }
    if (String(val) === '-9') return 'No data available'
    return val
}

function headerClass(proj: ProjectEntry) {
    return projectMatchesHighlight(proj)
        ? 'justify-between bg-accent'
        : 'justify-between bg-grey-3'
}

function groupHeaderClass(projs: ProjectEntry[]) {
    return projs.some(projectMatchesHighlight)
        ? 'justify-between bg-accent'
        : 'justify-between bg-grey-3'
}

function projectHeaderClass(proj: ProjectEntry) {
    const base = headerClass(proj)
    return projectDetailOpenState[proj.id] ? `${base} text-weight-bold` : base
}

const projects = computed<ProjectEntry[]>(() => {
    const uids: string[] = []
    const d: ProjectEntry[] = []
    if(data.value.RESTORATION_PROJECTS){
        data.value.RESTORATION_PROJECTS.forEach((cell) =>{
            if (cell.UIDs){
                cell.UIDs.split(", ").forEach((uid) =>{ 
                    uids.push(uid)
                    d.push({
                        id: uid,
                        uid : restorationMap.value.get(uid) 
                    })
                })
    
            }
        })
    }

    d.sort((a, b) => {
        const aName = String(a.uid?.Name ?? '').toLowerCase()
        const bName = String(b.uid?.Name ?? '').toLowerCase()
        if (aName < bName) return -1
        if (aName > bName) return 1
        return 0
    })

    return d
})

const hasSelectedProjects = computed(() => {
    if (!selectedHighlight.value) return false
    return projects.value.some(projectMatchesHighlight)
})

watch(hasSelectedProjects, (hasSelected) => {
    if (hasSelected) {
        projectDetailsOpen.value = true
    }
})

const projectsGrouped = computed(() => {
    const groups = new Map<string, ProjectEntry[]>()
    for (const proj of projects.value) {
        const name = String(proj.uid?.Name ?? '')
        const baseMatch = name.match(/^(.+?)\s*#\d+$/)
        const baseName = baseMatch ? baseMatch[1].trim() : name
        if (!groups.has(baseName)) groups.set(baseName, [])
        groups.get(baseName)!.push(proj)
    }
    return Array.from(groups.entries()).map(([baseName, projs]) => ({
        baseName,
        projects: projs,
        isGroup: projs.length > 1
    }))
})

const projectsByHabitatType = computed<Record<string, ProjectEntry[]>>(() => {
    const d: Record<string, ProjectEntry[]> = {}
    for (const [typeid, habitat] of Object.entries(habitat_types)) {
        // annotate projs as an array of ProjectEntry and handle lists
        const projs: ProjectEntry[] = projects.value.filter((proj) => {
            if (!proj.uid) return false
            const val = proj.uid.HabID;
            if (typeof val === 'string') {
                return val.split(",").map(s => s.trim()).includes(typeid);
            }
            return val == typeid;
        });

        if (projs.length > 0) {
            if (!d[habitat]){
                d[habitat] = []
            }
            d[habitat] = d[habitat].concat(projs)
        } 
    }
    return d
})

const projectsBySpecies = computed<Record<string, ProjectEntry[]>>(() => {
    const d: Record<string, ProjectEntry[]> = {}
    for (const [code, spp] of Object.entries(restoration_spp)) {
        // annotate projs as an array of ProjectEntry
        const projs: ProjectEntry[] = projects.value.filter((proj) => {
            if (!proj.uid) return false
            const val = proj.uid.SPP;
            if (typeof val === 'string') {
                // some entries store multiple codes in a comma‑separated string
                return val.split(",").map(s => s.trim()).includes(code);
            }
            // fallback to direct comparison (number or single string)
            return val == code;
        });

        if (projs.length > 0) {
            if (!d[spp]){
                d[spp] = []
            }
            d[spp] = d[spp].concat(projs)
        } 
    }
    return d
})

const projectsByType = computed<Record<string, ProjectEntry[]>>(() => {
    const d: Record<string, ProjectEntry[]> = {}
    for (const [code, type] of Object.entries(restoration_projtype)) {
        // annotate projs as an array of ProjectEntry
        const projs: ProjectEntry[] = projects.value.filter(
            (proj) => proj.uid && proj.uid.PTID == code
        )

        if (projs.length > 0) {
            if (!d[type]){
                d[type] = []
            }
            d[type] = d[type].concat(projs)
        } 
    }
    return d
})

// Maps each restoration_spp code to a group category
const sppTypeGroups: Record<string, 'Plants' | 'Fish' | 'Birds' | 'Shellfish' | 'Other'> = {
    "-9": "Other",
    // Plants
    "2": "Plants", "6": "Plants", "11": "Plants", "13": "Plants", "18": "Plants",
    "24": "Plants", "29": "Plants", "32": "Plants", "33": "Plants", "34": "Plants",
    "49": "Plants", "50": "Plants", "52": "Plants", "55": "Plants", "58": "Plants",
    "61": "Plants", "63": "Plants", "64": "Plants", "69": "Plants", "71": "Plants",
    "72": "Plants", "78": "Plants", "107": "Plants", "125": "Plants",
    // Birds
    "26": "Birds", "28": "Birds", "30": "Birds", "31": "Birds", "38": "Birds",
    "39": "Birds", "41": "Birds", "42": "Birds", "62": "Birds", "81": "Birds",
    "82": "Birds", "87": "Birds", "93": "Birds", "95": "Birds", "96": "Birds",
    "101": "Birds", "102": "Birds", "105": "Birds", "108": "Birds", "109": "Birds",
    "111": "Birds", "112": "Birds", "113": "Birds", "114": "Birds", "115": "Birds",
    "117": "Birds", "118": "Birds", "119": "Birds", "120": "Birds", "121": "Birds",
    "122": "Birds", "123": "Birds", "126": "Birds", "127": "Birds", "128": "Birds",
    "129": "Birds",
    // Fish
    "1": "Fish", "4": "Fish", "7": "Fish", "9": "Fish", "10": "Fish",
    "12": "Fish", "14": "Fish", "15": "Fish", "16": "Fish", "17": "Fish",
    "19": "Fish", "20": "Fish", "21": "Fish", "23": "Fish", "27": "Fish",
    "35": "Fish", "40": "Fish", "43": "Fish", "44": "Fish", "45": "Fish",
    "46": "Fish", "48": "Fish", "51": "Fish", "53": "Fish", "54": "Fish",
    "56": "Fish", "57": "Fish", "60": "Fish", "65": "Fish", "66": "Fish",
    "68": "Fish", "70": "Fish", "73": "Fish", "74": "Fish", "75": "Fish",
    "76": "Fish", "79": "Fish", "80": "Fish", "83": "Fish", "84": "Fish",
    "85": "Fish", "86": "Fish", "88": "Fish", "90": "Fish", "91": "Fish",
    "92": "Fish", "100": "Fish", "103": "Fish", "106": "Fish", "110": "Fish",
    "116": "Fish", "124": "Fish", "130": "Fish",
    // Shellfish (bivalves, gastropods, crustaceans)
    "3": "Shellfish", "5": "Shellfish", "8": "Shellfish", "25": "Shellfish",
    "36": "Shellfish", "37": "Shellfish", "47": "Shellfish", "59": "Shellfish",
    "67": "Shellfish", "77": "Shellfish", "89": "Shellfish", "97": "Shellfish",
    "98": "Shellfish",
    // Other (reptiles, mammals, other invertebrates)
    "22": "Other", "94": "Other", "99": "Other", "104": "Other", "131": "Other",
}

const SPP_GROUP_ORDER = ['Plants', 'Fish', 'Birds', 'Shellfish', 'Other'] as const
type SppGroup = typeof SPP_GROUP_ORDER[number]

const projectsBySpeciesGrouped = computed<Record<SppGroup, Record<string, ProjectEntry[]>>>(() => {
    const result = Object.fromEntries(SPP_GROUP_ORDER.map(g => [g, {}])) as Record<SppGroup, Record<string, ProjectEntry[]>>
    for (const [spp, projs] of Object.entries(projectsBySpecies.value)) {
        const code = getKeyByValue(restoration_spp, spp)
        const group: SppGroup = (code ? sppTypeGroups[code] : undefined) ?? 'Other'
        result[group][spp] = projs
    }
    return result
})

const projByHabColors = {
    "No habitat data available": "#CCCCCC",  
    "Upland": "#8B572A",                     
    "River/Stream/Pond": "#4A90E2",          
    "Freshwater Wetland": "#50E3C2",         
    "Dune/Beach": "#F5D76E",                 
    "Tidal Wetland": "#7ED321",              
    "Rocky Intertidal": "#4A4A4A",           
    "Soft Bottom/Mud": "#A67C52",            
    "Hard Bottom": "#2C3E50",                
    "Shellfish Habitat": "#D0021B",          
    "Seagrasses": "#0E9F6E",                 
    "Watershed/Estuary": "#417505",          
    "Offshore": "#003366",                   
    "Infrastructure": "#9B9B9B",             
    "Unknown": "#B8E986"  
}

const projBySppColors = {
    "No species data available": "#BDBDBD",

    // Mammals (browns)
    "River Otter": "#6D4C41",

    // Reptiles (teals)
    "Terrapin": "#00897B",

    // Birds (blues)
    "Piping Plover": "#1565C0",
    "Yellow-Crowned Night Heron": "#1E88E5",
    "Great Egret": "#2196F3",
    "American Bittern": "#42A5F5",
    "Spotted Sandpiper": "#64B5F6",
    "Great Blue Heron": "#1976D2",
    "Common Tern": "#0D47A1",
    "Roseate Tern": "#90CAF9",
    "Snowy Egret": "#5E92F3",
    "Black-Crowned Night Heron": "#2C7FB8",
    "American Black Duck": "#4F9DD9",
    "Willet": "#73B3E7",
    "Redhead": "#A6CEE3",  // duck (bird)
    "Gadwall": "#74A9CF",
    "Little Blue Heron": "#6BAED6",
    "Saltmarsh Sparrow": "#4292C6",
    "Glossy Ibis": "#2171B5",
    "Osprey": "#08519C",
    "Seaside Sparrow": "#9ECAE1",
    "Green Heron": "#C6DBEF",
    "Least Sandpiper": "#3182BD",
    "Solitary Sandpiper": "#6BAED6",
    "Semipalmated Sandpiper": "#9ECAE1",
    "Mallard": "#4292C6",
    "Willow - State Listed Flycatcher": "#2171B5",  // Willow Flycatcher (bird)
    "Green-Winged Teal": "#08519C",
    "Greater Yellowlegs": "#5DA5E0",
    "Lesser Yellowlegs": "#3C8FD8",
    "Scarlet Ibis": "#2E7CBF",
    "Semipalmated Plover": "#1F6AAE",
    "Virginia Rail": "#10579C",
    "Eastern Black Rail": "#7FB3E6",
    "Nelsons Sparrow": "#A7C7ED",
    "Coastal Plain Swamp Sparrow": "#6EA7E2",
    "Clapper Rail": "#4F95D9",

    // Plants (greens)
    "Smooth Cordgrass": "#0E9F6E",
    "Marshhay Cordgrass": "#2E7D32",
    "Eelgrass": "#4CAF50",
    "Wild Celery": "#66BB6A",
    "Salt-Marsh Bulrush": "#81C784",
    "Big Cordgrass": "#1B5E20",
    "Narrow-Leaved Cattail": "#43A047",
    "Softstem Bulrush": "#6DBB7A",
    "Switchgrass": "#2F9E44",
    "Widgeongrass": "#3FA34D",
    "Smooth - Invasive Cordgrass": "#7CB342",
    "Sago Pondweed": "#8BC34A",
    "Redhead Grass": "#9CCC65",
    "Alkaligrass": "#AED581",
    "Common Reed": "#66A06F",
    "Red Osier Dogwood": "#3F9142",
    "Seashore Saltgrass": "#2D8659",
    "Black Needlerush": "#4E9A51",
    "Marsh Elder": "#358856",
    "Marsh Hibiscus": "#5DAE6E",
    "Olney Three-Square": "#68B774",
    "White Alder": "#79C184",
    "Spike-Rush": "#59AE6B",
    "Horned Pondweed": "#48A863",

    // Invertebrates (purples)
    "Hard Clam": "#6A1B9A",
    "Blue Crab": "#8E24AA",
    "Eastern Oyster": "#9C27B0",
    "Horseshoe Crab": "#AB47BC",
    "Eastem Softshell Clam": "#BA68C8", // kept original spelling
    "American Lobster": "#7B1FA2",
    "Bay Scallop": "#512DA8",
    "Brown Shrimp": "#8B5FBF",
    "Ribbed Mussel": "#7E57C2",
    "Blue Mussel": "#9575CD",
    "White Shrimp": "#B39DDB",
    "Grass Shrimp": "#A07CCF",
    "Northern Shrimp": "#9370DB",
    "Mud Snail": "#8367C7",

    // Fish (oranges)
    "Red Drum": "#EF6C00",
    "Bluefish": "#F57C00",
    "Striped Bass": "#FB8C00",
    "Black Drum": "#FFA000",
    "Alewives": "#FFB300",
    "Bay Anchovy": "#FF8F00",
    "White Perch": "#FF6F00",
    "Atlantic Croaker": "#F4A261",
    "American Eel": "#E67E22",
    "Largemouth Bass": "#ED8F36",
    "Blueback  Herring": "#FF9E54",  // note the double space retained
    "Mummichog": "#FFB860",
    "Cobia": "#F59F2F",
    "Atlantic Sturgeon": "#F18F01",
    "American Shad": "#FF7F11",
    "Sheepshead Minnow": "#FCA65D",
    "Atlantic Cod": "#FF8A3D",
    "Winter Flounder": "#FF944F",
    "Skipjack Shad": "#FFAC6E",
    "Rainbow Smelt": "#F9963E",
    "Atlantic Salmon": "#F8892D",
    "Summer Flounder": "#F5A25C",
    "Butterfish": "#F6A14D",
    "Oyster Toadfish": "#F39C3F",
    "Scup": "#F0A646",
    "Yellow Perch": "#FFAE53",
    "Arrow Goby": "#FFB766",
    "Atlantic Silverside": "#FFA447",
    "Fourspine Stickleback": "#FFB36A",
    "Brook Trout": "#FF8C2E",
    "Pink Shrimp": "#FBA469",  // inverts but visually close to fish orange family—tell me if you prefer it purple
    "Spot": "#FF983D",
    "Spanish Mackerel": "#F08C2B",
    "Spotted Seatrout": "#F69B45",
    "Silver Perch": "#FFA64F",
    "Black Seabass": "#FF8626",
    "Weakfish": "#F09B3E",
    "Atlantic Herring": "#F7A654",
    "Atlantic Menhaden": "#F39B3E",
    "Sea Lamprey": "#E9851D",
    "Sea-Run Brown Trout": "#FB8E3C",
    "Pollock": "#F3A358",
    "Threespine Stickleback": "#F7A24A",
    "Northern Searobin": "#F59B3D",
    "Gulf Killifish": "#F7A258",
    "Northern Pipefish": "#F69A43",
    "Atlantic Tomcod": "#F2922F",
    "Hickory Shad": "#FF9D4C",
    "Rainbow Trout": "#FF8F33",
    "Pinfish": "#FF7F1A",
    "Tautog": "#F07818",
    "Sheepshead": "#F5892E"
}

const projByTypeColors = {
    "No project type data available": "#BDBDBD",  
    "Mitigation": "#F57C00",                      
    "Protection/Acquisition": "#43A047",          
    "Research/Monitoring": "#1E88E5",             
    "Restoration": "#00897B",                     
    "Public Access/Infrastructure": "#8E24AA",    
    "Planning/Outreach": "#FBC02D"    
}

const attrNames = {
    Name: "Name",
    Description: "Description",
    Project_Page: "Project Page",
    LINK: "Data source",
    PTID: "Project Type",
    HabID: "Habitat",
    SPP: "Species Benefitted",
    SRCE: "Source",
    STATUS: "Status",
    YEAR: "Year",
}

const l2AttrNames: Record<string, string> = l2AttrNamesJson

const attrOrder = Object.keys(attrNames)

const l2DialogOpen = ref(false)
const selectedL2Project = ref<ProjectEntry | null>(null)

function openL2Dialog(proj: ProjectEntry) {
    selectedL2Project.value = proj
    l2DialogOpen.value = true
}

const l2DialogTitle = computed(() => selectedL2Project.value?.uid?.Name ?? 'Additional Details')

const l2DialogHtml = computed(() => {
    const proj = selectedL2Project.value
    if (!proj?.uid) return ''
    const l2Keys: string[] = proj.uid._l2Keys ?? Object.keys(l2AttrNames)
    const l1Keys = new Set([...attrOrder, ...hideMetrics])
    const rows = l2Keys
        .filter(key => !l1Keys.has(key) && proj.uid[key] != null && proj.uid[key] !== '' && String(proj.uid[key]) !== '-9')
        .map(key => {
            const label = l2AttrNames[key] ?? key
            const val = String(proj.uid[key])
            const display = val.startsWith('http')
                ? `<a href="${val}" target="_blank" rel="noopener noreferrer">Link</a>`
                : val
            return `<div class="q-mb-xs"><span style="font-weight:600">${label}</span>: ${display}</div>`
        })
    return rows.length ? rows.join('') : '<em>No additional details available.</em>'
})

const infoDialogs = ref({
    wetlands: {
        pName: "Coastal Marshes",
        model: false,
        text: "More info about the importance of coastal marshes."
    },
    numProjects :{
        pName: "Number of Projects",
        model: false,
        text: "The total number of projects is based on the count of individual projects as listed in the Project Details panel at the bottom.  The number of projects that benefit various habitats or species can be greater than the number of total projects because a single project can benefit multiple habitats and species.<div class='q-pt-sm'>The sections below list the number of coastal projects by habitat, species benefitted and type.  Selecting an item within each of these will highlight the corresponding projects in the Project Details section and in the map.</div><div class='q-pt-sm'>The project data is from a variety of sources including national, regional, state, and local agencies, NGOs, universities, and other organizations working on coastal and marine restoration.</div>"
    }
})

const goToSpSpotlight = (sp) =>{
    console.log(sp)
  let s
  // if the sp is being passed from monthgly data, just get the root of the sp
  if (sp.includes("_M")){
    s = sp.slice(0, 4)
  }
  else{
    s = sp
  }
  console.log(s)
  setActiveSpSpotlight(s)
  router.push('/spSpotlight')
}


onMounted(async () => {
  if (activeLayerID.value != 'RESTORATION_PROJECTS'){
    loadDataByID('RESTORATION_PROJECTS', version)
  }

  document.addEventListener('click', onDocumentClick)

  // Load RestorationProjects JSON and build UID → row map (L2 fields flattened)
  try {
    const resp = await fetch(`${import.meta.env.BASE_URL}RestorationProjects.json`);
    const json = await resp.json();
    const map = new Map<string, any>();
    for (const [uid, rec] of Object.entries(json)) {
      const flat = { UID: uid, ...rec, ...(rec.L2 ?? {}), L2: undefined };
      // Track which keys came from L2 for the details dialog
      flat._l2Keys = Object.keys(rec.L2 ?? {});
      // Normalize NAME → Name so grouping/display always uses 'Name'
      if (flat.NAME && !flat.Name) { flat.Name = flat.NAME; }
      map.set(uid, flat);
    }
    restorationMap.value = map;
  } catch (e) {
    console.error('Failed to load RestorationProjects.json:', e);
  } finally {
    calculating.value = false
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  clearRestorationProjectPoint()
  clearRestorationProjectHighlightPoints()
})
</script>
<style scoped>
.row-title{
    background-color: #cacaca;
}
.restoration-pane{
    max-width: 500px;
}
.details-header{
    background-color: #1D8177;
}
.details-card{
    border: 1px solid grey;
}
.circle{
    border-radius:50%;
}
.two-column-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem 1rem;
}
.two-column-list > * {
    break-inside: avoid;
}
.rounded{
    border-radius: 3px;
}
.proj-expander{
    border: 1px solid #989999;
}
.proj-det-expander{
    border: 1px solid #1D8177;
}
.project-detail-header{
    width: 100%;
    min-width: 0;
}
.project-detail-title{
    min-width: 0;
}
.bar-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.35rem 0.75rem;
}
.bar-item {
    padding: 1px 3px;
    border-radius: 3px;
    border: 1px solid transparent;
}
.bar-item-selected {
    border-color: #1D8177;
    background-color: rgba(29, 129, 119, 0.08);
}
.bar-row {
    display: flex;
    align-items: center;
    gap: 5px;
}
.bar-track {
    flex: 0 0 60px;
    background-color: #e0e0e0;
    height: 11px;
    border-radius: 3px;
    overflow: hidden;
}
.bar-fill {
    height: 100%;
    border-radius: 3px;
    background-color: var(--q-secondary);
    transition: width 0.3s ease;
}
.bar-meta {
    font-size: 11px;
    line-height: 1.3;
    flex: 1;
}
</style>